/* eslint-disable consistent-return */
import {
  AudioTrackAddEvent,
  AudioTrackChangeEvent,
  useMediaPlayer,
} from '@vidstack/react'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'

import { useLocalStorage } from '@/core/utils/localStorage/useLocalStorage'

/**
 * Sets the default audio track locale for media player.
 */
export function AudioTrackLocale({
  originalAudioLanguage,
}: {
  originalAudioLanguage?: string
}) {
  const player = useMediaPlayer()
  const { locale } = useIntl()
  const [playerAudioLocale = locale, setPlayerAudioLocale] = useLocalStorage(
    'media-player-audio-locale',
  )

  useEffect(() => {
    if (!player) return

    const displayNames = new Intl.DisplayNames(['en'], {
      type: 'language',
      languageDisplay: 'standard',
    })

    const getDisplayName = (arg: string) => {
      try {
        return displayNames.of(arg) || arg
      } catch {
        return arg
      }
    }

    // Escapes user/content-provided strings for safe use in RegExp.
    // Example: "Dutch [NL]" => "Dutch \[NL\]" (prevents regex syntax errors).
    const escapeRegExp = (value: string) =>
      value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    const normalizeString = (str: string) =>
      str.trim().toLowerCase().replace(/_/g, '-')

    let hasExactMatch = false

    const setDefaultAudioTrack = (event: AudioTrackAddEvent & Event) => {
      const track = event.detail
      const trackLabel = normalizeString(track.label)
      if (!trackLabel) return

      // If the original track's language matches the user's locale preference,
      // select the original track directly and skip dubbed tracks.
      if (trackLabel === 'original' && originalAudioLanguage) {
        const [baseOriginal] = normalizeString(originalAudioLanguage).split('-')
        const [baseLocale] = normalizeString(playerAudioLocale).split('-')
        const originalDisplayBase = normalizeString(
          getDisplayName(baseOriginal),
        )
        const localeDisplayBase = normalizeString(getDisplayName(baseLocale))

        if (
          baseOriginal === baseLocale ||
          originalDisplayBase === localeDisplayBase
        ) {
          hasExactMatch = true
          track.selected = true
          return
        }
      }

      const normalizedPlayerAudioLocale = normalizeString(playerAudioLocale)
      const exactCodeMatch = trackLabel === normalizedPlayerAudioLocale
      const exactDisplayNameMatch =
        trackLabel ===
        normalizeString(getDisplayName(normalizedPlayerAudioLocale))

      const [baseLanguageCode] = normalizedPlayerAudioLocale.split('-')
      const baseLanguageDisplayName = normalizeString(
        getDisplayName(baseLanguageCode),
      )

      const isBaseLocale = !normalizedPlayerAudioLocale.includes('-')

      // Fallback match for dialect labels when we only have a base locale.
      // Example: "en" must NOT match "French" (contains "en").
      // Example: "hu" should match "Hungarian (Hungary)".
      const indirectMatch =
        !hasExactMatch &&
        (isBaseLocale || trackLabel.includes('(')) &&
        new RegExp(
          `^${escapeRegExp(baseLanguageDisplayName)}(?:$|\\s*\\()`,
          'i',
        ).test(trackLabel)

      if (!hasExactMatch && (exactCodeMatch || exactDisplayNameMatch)) {
        hasExactMatch = true
        track.selected = true
        return
      }

      if (indirectMatch) {
        track.selected = true
      }
    }

    player.audioTracks.addEventListener('add', setDefaultAudioTrack)
    return () => {
      player.audioTracks.removeEventListener('add', setDefaultAudioTrack)
    }
  }, [player, playerAudioLocale, originalAudioLanguage])

  useEffect(() => {
    if (!player) return

    const saveAudioTrackSelection = (event: AudioTrackChangeEvent & Event) => {
      const { prev, current } = event.detail

      if (!current?.label) return

      const label =
        current.label === 'original' && originalAudioLanguage
          ? originalAudioLanguage
          : current.label

      if (prev && label !== 'original') {
        setPlayerAudioLocale(label)
      }
    }

    player.audioTracks.addEventListener('change', saveAudioTrackSelection)
    return () => {
      player.audioTracks.removeEventListener('change', saveAudioTrackSelection)
    }
  }, [originalAudioLanguage, player, setPlayerAudioLocale])

  return null
}
